import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyWorkflows,
  CompanyWorkflowsLeaveManagementPage,
  CompanyWorkflowsAssetManagementPage,
  CompanyWorkflowsOvertimeRequestsPage,
} from "@/utils/lazyLoadComponents";
import WorkflowRequestsTable from "@/pages/(home)/(company)/workflows/_components/WorkflowRequestTable";
import WorkflowVersionsList from "@/pages/(home)/(company)/workflows/_components/WorkflowVersionsList";

// ─── Path Segments ────────────────────────────────────────────────────────────
// The workflow BUILDER now lives in Studio (routes.studio.ts). This module keeps
// only the listing + instances + versions views.
export const COMPANY_WORKFLOWS_DYNAMIC_ID_KEY = "id";

export enum COMPANY_WORKFLOWS_PATH_SEGMENTS {
  ROOT = "workflows",
  LEAVE_MANAGEMENT = "leave-management",
  ASSET_MANAGEMENT = "asset-management",
  OVERTIME_REQUESTS = "overtime-requests",
  DETAILS = `:${COMPANY_WORKFLOWS_DYNAMIC_ID_KEY}`,
  INSTANCE = "instance",
  VERSIONS = "versions",
}

// ─── Route Definition (inside Layout) ────────────────────────────────────────
export const companyWorkflowsRoutes: RouteObject = {
  path: COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Workflows" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyWorkflows,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () =>
            redirect(COMPANY_WORKFLOWS_PATHS.LEAVE_MANAGEMENT),
        },
        {
          path: COMPANY_WORKFLOWS_PATH_SEGMENTS.LEAVE_MANAGEMENT,
          handle: { breadcrumb: "Leave Management" },
          Component: CompanyWorkflowsLeaveManagementPage,
        },
        {
          path: COMPANY_WORKFLOWS_PATH_SEGMENTS.ASSET_MANAGEMENT,
          handle: { breadcrumb: "Asset Management" },
          Component: CompanyWorkflowsAssetManagementPage,
        },
        {
          path: COMPANY_WORKFLOWS_PATH_SEGMENTS.OVERTIME_REQUESTS,
          handle: { breadcrumb: "Overtime Requests" },
          Component: CompanyWorkflowsOvertimeRequestsPage,
        },
      ],
    },
    {
      path: COMPANY_WORKFLOWS_PATH_SEGMENTS.INSTANCE,
      children: [
        {
          path: COMPANY_WORKFLOWS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Workflow Instances" },
          Component: WorkflowRequestsTable,
        },
      ],
    },
    {
      path: COMPANY_WORKFLOWS_PATH_SEGMENTS.VERSIONS,
      children: [
        {
          path: COMPANY_WORKFLOWS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Workflow Versions" },
          Component: WorkflowVersionsList,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_WORKFLOWS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT)),
  LEAVE_MANAGEMENT: withCompanyPrefix(
    addSlashes(
      COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
      COMPANY_WORKFLOWS_PATH_SEGMENTS.LEAVE_MANAGEMENT,
    ),
  ),
  ASSET_MANAGEMENT: withCompanyPrefix(
    addSlashes(
      COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
      COMPANY_WORKFLOWS_PATH_SEGMENTS.ASSET_MANAGEMENT,
    ),
  ),
  OVERTIME_REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
      COMPANY_WORKFLOWS_PATH_SEGMENTS.OVERTIME_REQUESTS,
    ),
  ),
  INSTANCE: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
        COMPANY_WORKFLOWS_PATH_SEGMENTS.INSTANCE,
        String(id),
      ),
    ),
  /**
   * Instance list URL, optionally filtered by version.
   */
  INSTANCE_WITH: (
    id: string | number,
    params?: { versionId?: string | number },
  ) => {
    const base = withCompanyPrefix(
      addSlashes(
        COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
        COMPANY_WORKFLOWS_PATH_SEGMENTS.INSTANCE,
        String(id),
      ),
    );
    if (params?.versionId === undefined || params.versionId === null) {
      return base;
    }
    return `${base}?versionId=${params.versionId}`;
  },
  VERSIONS: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_WORKFLOWS_PATH_SEGMENTS.ROOT,
        COMPANY_WORKFLOWS_PATH_SEGMENTS.VERSIONS,
        String(id),
      ),
    ),
});
